import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import SeoHead from '@/components/feature/SeoHead';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://www.discoveringmarche.com';

const GUIDA_JSONLD = [
  {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: 'Come orientarsi nel mercato immobiliare delle Marche — Guida completa | Discovering Marche',
    description: 'Guida passo dopo passo per orientarsi nel mercato immobiliare dei borghi storici delle Marche: dalla ricerca alla firma del rogito.',
    url: `${SITE_URL}/guida-acquisto`,
    step: [
      { '@type': 'HowToStep', position: 1, name: 'Ricerca immobile', text: 'Definisci le caratteristiche dell\'immobile desiderato.' },
      { '@type': 'HowToStep', position: 2, name: 'Visita e valutazione', text: 'Visita gli immobili selezionati e valuta la conformità urbanistica.' },
      { '@type': 'HowToStep', position: 3, name: 'Proposta', text: 'Presenta una proposta formale con caparra confirmatoria.' },
      { '@type': 'HowToStep', position: 4, name: 'Compromesso', text: 'Firma il contratto preliminare dal notaio.' },
      { '@type': 'HowToStep', position: 5, name: 'Rogito notarile', text: 'Firma l\'atto definitivo e ricevi le chiavi.' },
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'Guida immobiliare', item: `${SITE_URL}/guida-acquisto` },
    ],
  },
];

const STEPS = [
  { key: '1', icon: 'ri-search-line', color: 'bg-terracotta-50 text-terracotta-300' },
  { key: '2', icon: 'ri-map-pin-line', color: 'bg-olive-50 text-olive-600' },
  { key: '3', icon: 'ri-file-text-line', color: 'bg-amber-50 text-amber-600' },
  { key: '4', icon: 'ri-pen-nib-line', color: 'bg-anthracite-50 text-anthracite-600' },
  { key: '5', icon: 'ri-key-line', color: 'bg-terracotta-50 text-terracotta-300' },
];

const GuidaAcquistoPage = () => {
  const { t } = useTranslation();
  const header = useScrollAnimation();
  const stepsRef = useScrollAnimation({ threshold: 0.05 });
  const sidebar = useScrollAnimation({ threshold: 0.05 });

  return (
    <main className="min-h-screen bg-white">
      <SeoHead
        title="Guida immobiliare Marche — Orientarsi nei borghi storici | Discovering Marche"
        description="Guida completa per orientarsi nel mercato immobiliare dei borghi storici delle Marche: dalla ricerca alla firma del rogito. 5 passi con Discovering Marche."
        keywords="guida immobiliare Marche, borghi storici Marche, rogito notarile Italia, iter notarile Marche, comprare casa Marche"
        canonical="/guida-acquisto"
        jsonLd={GUIDA_JSONLD}
      />
      {/* Hero */}
      <section className="relative h-[50vh] min-h-[380px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Italian%20real%20estate%20purchase%20guide%20notary%20contract%20signing%20elegant%20professional%20Marche%20Italy%20property%20buying%20process%20step%20by%20step&width=1920&height=800&seq=guidaHero&orientation=landscape"
            alt="Guida acquisto casa Marche — comprare casa borghi Marche"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/10"></div>
        </div>
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 pb-14">
          <p className="font-sans text-xs tracking-widest uppercase text-terracotta-200 mb-4">{t('guida.overline')}</p>
          <h1 className="font-serif text-4xl md:text-6xl text-white font-light max-w-3xl leading-tight">
            {t('guida.title')}
          </h1>
        </div>
      </section>

      {/* Content */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Main content */}
          <div className="lg:col-span-2">
            <div
              ref={header.ref}
              className={`fade-in-up ${header.isVisible ? 'visible' : ''} mb-12`}
            >
              <p className="font-sans text-lg text-anthracite-500 leading-relaxed border-l-4 border-terracotta-300 pl-6 mb-8">
                {t('guida.intro')}
              </p>
            </div>

            {/* Steps */}
            <div
              ref={stepsRef.ref}
              className={`fade-in-up ${stepsRef.isVisible ? 'visible' : ''} space-y-12`}
            >
              {STEPS.map((step, idx) => (
                <div key={step.key} className="flex gap-8">
                  <div className="flex flex-col items-center">
                    <div className={`w-14 h-14 flex items-center justify-center rounded-2xl ${step.color} flex-shrink-0`}>
                      <i className={`${step.icon} text-2xl`}></i>
                    </div>
                    {idx < STEPS.length - 1 && (
                      <div className="w-px flex-1 bg-ivory-200 mt-4"></div>
                    )}
                  </div>
                  <div className="pb-12">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="font-sans text-xs tracking-widest uppercase text-terracotta-300">
                        {t('guida.step')} {step.key}
                      </span>
                    </div>
                    <h4 id={`step-${step.key}`} className="font-serif text-2xl text-anthracite-700 font-light mb-4">
                      <a href={`#step-${step.key}`} className="hover:text-terracotta-300 transition-colors">
                        {t(`guida.step${step.key}.title`)}
                      </a>
                    </h4>
                    <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-4">
                      {t(`guida.step${step.key}.text`)}
                    </p>

                    {/* Step image */}
                    <div className="rounded-xl overflow-hidden aspect-[16/9] mt-6">
                      <img
                        src={`https://readdy.ai/api/search-image?query=Italian%20real%20estate%20step%20$%7Bstep.key%7D%20process%20$%7B%5Bproperty%20search%2C%20property%20visit%2C%20purchase%20offer%2C%20notary%20deed%2C%20key%20handover%5D%5Bidx%5D%7D%20Marche%20Italy%20elegant%20professional&width=700&height=400&seq=guidaStep${step.key}&orientation=landscape`}
                        alt={t(`guida.step${step.key}.title`)}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Sidebar */}
          <aside
            ref={sidebar.ref}
            className={`fade-in-up ${sidebar.isVisible ? 'visible' : ''} lg:col-span-1`}
          >
            <div className="sticky top-24 space-y-6">
              {/* CTA */}
              <div className="bg-terracotta-50 border border-terracotta-200 rounded-2xl p-6">
                <h3 className="font-serif text-xl text-anthracite-800 mb-3">{t('guida.sidebar.cta.title')}</h3>
                <p className="font-sans text-sm text-anthracite-500 mb-5 leading-relaxed">{t('guida.sidebar.cta.text')}</p>
                <Link
                  to="/contatti"
                  className="block text-center font-sans text-sm tracking-widest uppercase px-6 py-3 bg-terracotta-300 text-white hover:bg-terracotta-400 transition-colors rounded-full cursor-pointer whitespace-nowrap"
                >
                  {t('guida.sidebar.cta.btn')}
                </Link>
              </div>

              {/* Move to Marche link */}
              <div className="bg-anthracite-800 rounded-2xl p-6">
                <p className="font-sans text-xs tracking-widest uppercase text-white/40 mb-3">Per acquirenti esteri</p>
                <h3 className="font-serif text-lg text-white mb-3">Move to Marche</h3>
                <p className="font-sans text-sm text-white/60 mb-5 leading-relaxed">{t('guida.sidebar.move.text')}</p>
                <Link
                  to="/move-to-marche"
                  className="block text-center font-sans text-sm tracking-widest uppercase px-6 py-3 border border-white/30 text-white hover:bg-white/10 transition-colors rounded-full cursor-pointer whitespace-nowrap"
                >
                  {t('guida.sidebar.move.btn')}
                </Link>
              </div>

              {/* Index */}
              <div className="bg-ivory-50 border border-ivory-200 rounded-2xl p-6">
                <p className="font-sans text-xs tracking-widest uppercase text-anthracite-400 mb-4">{t('guida.indice')}</p>
                <div className="space-y-2">
                  {STEPS.map((step) => (
                    <a
                      key={step.key}
                      href={`#step-${step.key}`}
                      className="flex items-center gap-3 font-sans text-sm text-anthracite-600 hover:text-terracotta-300 transition-colors cursor-pointer"
                    >
                      <span className="w-5 h-5 flex items-center justify-center rounded-full bg-terracotta-50 text-terracotta-300 text-xs font-semibold flex-shrink-0">
                        {step.key}
                      </span>
                      {t(`guida.step${step.key}.title`)}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </main>
  );
};

export default GuidaAcquistoPage;
